import React, { useEffect, useState } from "react";

import { ApolloQueryResult } from "@apollo/client";
import {
  Button,
  Callout,
  Colors,
  Icon,
  Intent,
  OverlayToaster,
  Position,
} from "@blueprintjs/core";
import { compareDesc, differenceInMinutes } from "date-fns";

import acceptReview from "@/../../hypetrain/static/img/accept-review.svg";
import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { LazyImg } from "@/components/helpers/ui/LazyImg";
import { isProgramLead } from "@/components/helpers/workspace/permissions";
import { Box, Flex } from "@/components/layout/flexbox";
import { ComponentContextButton } from "@/components/pages_logged_in/program/components/ComponentContextButton";
import { PROGRAM_COMPONENT_ICONS } from "@/components/pages_logged_in/program/components/component-utils";
import {
  canEndEarlyComponent,
  canGrantExtension,
} from "@/components/pages_logged_in/program/components/componentMetadata";
import { DateTimeDisplay } from "@/components/pieces/DateTimeDisplay";
import { Card } from "@/components/ui/card";
import { CARD_CLASSES, MIDTEXT_COLOR, RED } from "@/css/constants";
import {
  ComponentTypeEnum,
  ProgramComponentsQuery,
  ProgramQuery,
  ProgramQueryDocument,
  ProgramType,
  useCompleteSetupProgramComponentsMutation,
} from "@/graphql";
import { cn } from "@/lib/utils";
import {
  isLeadComponent,
  LeadComponent,
  MainComponent,
  MainProgram,
  ParticipantComponent,
  ProgramDialog,
} from "@/types/programs";

import { ModifyProgramDialogs } from "./ModifyProgramDialogs";
import { allowEditComponent } from "./utils";

type ProgramComponent = LeadComponent | MainComponent | ParticipantComponent;

interface ProgramComponentsPanelProps {
  component: ProgramComponent;
  program: MainProgram;
  onDelete: (component: ProgramComponent) => void;
  onEdit: (component: ProgramComponent) => void;
  onClose: (component: ProgramComponent) => void;
  onGrantExtension?: (component: ProgramComponent) => void;
}

function ProgramComponentsPanel({
  component,
  program,
  onDelete,
  onEdit,
  onClose,
  onGrantExtension,
}: ProgramComponentsPanelProps) {
  const componentStatus = component?.status;
  const startDate = component?.targetStartDate;
  const endDate = component?.targetDueDate || component?.targetEndDate;
  const isGroupMeeting = component?.type === ComponentTypeEnum.GroupMeeting;
  const showEndDate = !isGroupMeeting && endDate;
  const showDuration = isGroupMeeting;
  const isLead = isProgramLead(program?.id);
  const duration =
    endDate && startDate
      ? differenceInMinutes(new Date(endDate), new Date(startDate))
      : undefined;
  const formatString = isGroupMeeting ? "MMM d, h:mm a" : "MMM d";
  const isStartDatePassed = startDate
    ? compareDesc(new Date(startDate), new Date()) > 0
    : false;
  const isProgramClosed = Boolean(program?.closedAt);
  const allowsGrantExtension = canGrantExtension(
    component,
    isLead,
    program?.status,
  );
  const isEditable = allowEditComponent({
    component,
    isProgramClosed,
    isProgramLead: isLead,
    program,
  });
  const hasAction = isEditable || allowsGrantExtension;

  const componentType = component?.type;
  const icon = componentType
    ? PROGRAM_COMPONENT_ICONS?.[componentType]?.icon
    : undefined;

  return (
    <Flex
      flex={1}
      py={12}
      px={3}
      className={CARD_CLASSES}
      css={{ background: Colors.WHITE }}
      justifyContent="space-between"
      mb={12}
      style={{ gap: 8 }}
    >
      <Flex
        className="xs:flex-col md:flex-row xs:items-start md:items-center gap-2"
        justifyContent="space-between"
        flex={1}
      >
        <div className="flex items-center gap-2 flex-1">
          <Box>{icon && <LazyImg width={32} src={icon} />}</Box>
          <Flex
            fontSize={16}
            fontWeight={500}
            flex={1}
            alignItems="flex-start"
            flexDirection="column"
          >
            <Box>{component?.name}</Box>
            {isStartDatePassed && (
              <Box fontSize={14} fontWeight={400} color={RED}>
                Start date has already passed
              </Box>
            )}
          </Flex>
        </div>
        <div className="flex xs:flex-col md:flex-row xs:items-start md:items-center xs:gap-2 md:gap-0">
          <div
            className={cn(
              "text-sm font-normal text-midtext invisible xs:hidden md:block",
              {
                "visible xs:block": component.optional,
              },
            )}
          >
            Optional
            <span className="xs:hidden md:inline">&nbsp;・&nbsp;</span>
          </div>

          {(startDate || endDate) && (
            <Flex
              fontSize={14}
              color={isStartDatePassed ? RED : MIDTEXT_COLOR}
              flexShrink={0}
            >
              {startDate && (
                <DateTimeDisplay formatString={formatString} time={startDate} />
              )}
              {startDate && showEndDate && <Box mx={1}>-</Box>}
              {showEndDate && (
                <DateTimeDisplay formatString="MMM d" time={endDate} />
              )}
              {showDuration && <Box ml={1}> ・ {duration} mins</Box>}
            </Flex>
          )}
        </div>
      </Flex>
      {hasAction && (
        <div className="flex items-center shrink-0">
          <ComponentContextButton
            component={component}
            onDelete={onDelete}
            onEdit={onEdit}
            onClose={onClose}
            couldEndEarly={canEndEarlyComponent(
              componentStatus,
              isGroupMeeting,
              isLead,
            )}
            canGrantExtension={allowsGrantExtension}
            canEdit={isEditable}
            onGrantExtension={onGrantExtension}
          />
        </div>
      )}
    </Flex>
  );
}

interface DesignProgramPanelProps {
  data: {
    icon: string;
    title: string;
    description: string;
  };
  program: MainProgram;
  components: ProgramComponent[];
  hasTimelineSlipped: boolean;
  onAddComponent: () => void;
  onEditComponent: (component: ProgramComponent) => void;
  onDeleteComponent: (component: ProgramComponent) => void;
  onCloseComponent: (component: ProgramComponent) => void;
  refetchProgram: () => Promise<ApolloQueryResult<ProgramQuery>>;
  refetchProgramComponents: () => Promise<
    ApolloQueryResult<ProgramComponentsQuery>
  >;
}

export function DesignProgramPanel({
  data,
  program,
  components,
  hasTimelineSlipped,
  onAddComponent,
  onEditComponent,
  onDeleteComponent,
  onCloseComponent,
  refetchProgram,
  refetchProgramComponents,
}: DesignProgramPanelProps) {
  const toasterRef = React.useRef<OverlayToaster>(null);
  const { icon, title, description } = data || {};

  const onError = (error: Error) => {
    toasterRef.current?.show({
      message:
        error.message.replace("[SKIP SENTRY]:", "") ||
        "Failed to complete setup program components",
      intent: Intent.DANGER,
    });
  };

  const [completeSetupProgramComponents, { loading }] =
    useCompleteSetupProgramComponentsMutation({
      onCompleted: (data) => {
        const ok = data?.completeSetupProgramComponents?.ok;
        if (!ok) {
          onError(Error("Something went wrong. Please try again."));
        }
      },
      onError,
      refetchQueries: [
        {
          query: ProgramQueryDocument,
          variables: {
            programId: program.id,
          },
        },
      ],
    });

  const hasCompletedSetup = components.some(
    (obj) => obj.isSetupComplete === true,
  );

  const [dialogOpen, setDialogOpen] = useState<ProgramDialog>();

  const updateSlippedTimeline = () => {
    if (hasTimelineSlipped) {
      if (compareDesc(new Date(program.targetLaunchDate!), new Date()) >= 0) {
        setDialogOpen("update-program");
      } else {
        const slippedComponent = components?.find((obj) => {
          if (isLeadComponent(obj) && obj?.targetStartDate) {
            return compareDesc(new Date(obj?.targetStartDate), new Date()) >= 0;
          }
        });
        if (slippedComponent) {
          onEditComponent(slippedComponent);
        }
      }
    }
  };

  useEffect(() => {
    if (hasTimelineSlipped) {
      toasterRef.current?.show({
        className: "bg-red-50 border-red-300",
        message: (
          <div className="flex flex-col gap-1 px-[5px] py-px">
            <div>
              <Icon icon="warning-sign" size={14} color={RED} />
              <span className="ml-4 font-bold">
                Components cannot start in the past
              </span>
            </div>
            <div className="ml-7.5 font-normal">
              Please update either program launch date or the outdated
              components' start date.
            </div>
          </div>
        ),
        intent: Intent.NONE,
        isCloseButtonShown: false,
        timeout: 0,
      });
    } else {
      toasterRef.current?.clear();
    }
  }, [hasTimelineSlipped]);

  return (
    <>
      {hasTimelineSlipped && (
        <Card className="xs:p-5 mb-6 max-w-180 gap-0 md:p-6">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-4">
              <EmbedSvg src={acceptReview} height={56} width={56} />
              <div className="flex flex-col gap-1">
                <span className="text-lg font-semibold">
                  Oops, it looks like the timeline has slipped
                </span>
                <span className="font-normal text-gray-500">
                  Update the program launch date or modify the program component
                  timelines below.
                </span>
              </div>
            </div>
            <Button intent="primary" onClick={updateSlippedTimeline}>
              Update
            </Button>
          </div>

          <ModifyProgramDialogs
            dialogOpen={dialogOpen}
            refetch={() => {
              refetchProgram().then(() => {
                refetchProgramComponents();
              });
            }}
            onClosedDialog={() => {
              setDialogOpen(undefined);
            }}
            selectedProgram={program as ProgramType}
          />
        </Card>
      )}
      <Box
        flex={1}
        px={[16, 24]}
        py={[16, 30]}
        mb={24}
        className={CARD_CLASSES}
        css={{ background: Colors.WHITE }}
        maxWidth={720}
      >
        <Flex pb={24} className="bbs" alignItems="center" mb={24}>
          <Flex alignItems={"center"} width={"100%"}>
            <EmbedSvg src={icon} height={50} width={50} />
            <Box mx={12} flex={1}>
              <Box fontSize={20} fontWeight={600}>
                {title}
              </Box>
              <Box fontSize={16} color={MIDTEXT_COLOR}>
                {description}
              </Box>
            </Box>
          </Flex>
        </Flex>
        {components.map((component, index) => (
          <ProgramComponentsPanel
            key={index}
            program={program}
            component={component}
            onDelete={onDeleteComponent}
            onEdit={onEditComponent}
            onClose={onCloseComponent}
          />
        ))}
        {!hasCompletedSetup && (
          <Flex mt={24}>
            <Callout intent="warning" icon={null}>
              💡 There are components that need to be setup before you can
              complete
            </Callout>
          </Flex>
        )}
        {!program?.hasCompletedSetupComponents && (
          <Flex mt={24}>
            <Box textAlign={"center"} mr={3}>
              <Button
                large={true}
                intent="primary"
                loading={loading}
                disabled={!hasCompletedSetup || hasTimelineSlipped}
                onClick={() =>
                  completeSetupProgramComponents({
                    variables: { programId: program.id },
                  })
                }
                style={{
                  padding: "13px 48px",
                }}
              >
                All Done
              </Button>
            </Box>
            <Box textAlign={"center"}>
              <Button
                large={true}
                intent="none"
                loading={loading}
                onClick={onAddComponent}
                style={{
                  padding: "13px 48px",
                }}
              >
                Add Component
              </Button>
            </Box>
          </Flex>
        )}
        <OverlayToaster position={Position.BOTTOM_RIGHT} ref={toasterRef} />
      </Box>
    </>
  );
}
